import { Button, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import HistoryIcon from '@mui/icons-material/History';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import RowActions, { type RowAction } from '../../../../components/RowActions';
import type { ProjectAddress } from '../../../planning/types';
import type { SettingsProjectRow } from '../../types';
import { PROJECT_ROW_LABELS as LABELS } from '../../../../utils/labels';
import { formatAddressLine } from '../../../../utils/formatAddress';

interface AddressRowProps {
  address: ProjectAddress;
  readOnly?: boolean;
  onSetDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function AddressRow({ address, readOnly, onSetDefault, onEdit, onDelete }: AddressRowProps) {
  const label = formatAddressLine(address);
  const actions: RowAction[] = readOnly
    ? []
    : [
        ...(!address.is_default
          ? [{ label: LABELS.setDefaultAddress, icon: <StarBorderIcon fontSize="small" />, onClick: onSetDefault, className: 'text-text-disabled', testId: 'set-default-button' }]
          : []),
        { label: LABELS.editAddress, icon: <EditIcon fontSize="small" />, onClick: onEdit, testId: 'edit-button' },
        { label: LABELS.deleteAddress, icon: <DeleteOutlineIcon fontSize="small" />, onClick: onDelete, color: 'error' as const, testId: 'delete-button' },
      ];

  return (
    <div>
      <hr className="border-t border-border mb-0 mt-0" />
      <div className="flex items-center gap-2 pl-12 pr-3 py-3">
        <span className="flex-1 min-w-0 flex flex-col truncate">
          <span className="text-sm">{address.name}</span>
          <span className="text-xs text-text-secondary truncate">{label}</span>
        </span>
        {address.is_default && (
          <Tooltip title={LABELS.isDefaultAddress}>
            <span className="text-amber-400 flex items-center shrink-0">
              <StarIcon fontSize="small" />
            </span>
          </Tooltip>
        )}
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <RowActions actions={actions} />
          </div>
        )}
      </div>
    </div>
  );
}

interface ProjectRowProps {
  project: SettingsProjectRow;
  showDivider: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEdit?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDelete?: () => void;
  onViewHistory?: () => void;
  onAddAddress?: () => void;
  onEditAddress?: (address: ProjectAddress) => void;
  onDeleteAddress?: (id: string) => void;
  onSetDefaultAddress?: (id: string) => void;
}

export default function ProjectRow({
  project,
  showDivider,
  expanded,
  onToggle,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  onViewHistory,
  onAddAddress,
  onEditAddress,
  onDeleteAddress,
  onSetDefaultAddress,
}: ProjectRowProps) {
  const addresses = project.project_addresses;

  // Viewing history is read-only, not an edit/delete action, so it's shown for
  // archived projects too — deliberate exception to the "archived rows only show
  // unarchive" rule (see CLAUDE.md "Archived Records").
  const historyAction: RowAction[] = onViewHistory
    ? [{ label: LABELS.viewHistory, icon: <HistoryIcon fontSize="small" />, onClick: onViewHistory, testId: 'view-history-button' }]
    : [];

  const actions: RowAction[] = project.archived
    ? [
        ...(onUnarchive
          ? [{ label: LABELS.unarchiveProject, icon: <UnarchiveOutlinedIcon fontSize="small" />, onClick: onUnarchive, color: 'success' as const, testId: 'unarchive-button' }]
          : []),
        ...historyAction,
      ]
    : [
        ...(onEdit ? [{ label: LABELS.editProject, icon: <EditIcon fontSize="small" />, onClick: onEdit, color: 'primary' as const, testId: 'edit-button' }] : []),
        ...(project.has_machines
          ? onArchive
            ? [{ label: LABELS.archiveProject, icon: <ArchiveOutlinedIcon fontSize="small" />, onClick: onArchive, color: 'warning' as const, testId: 'archive-button' }]
            : []
          : onDelete
            ? [{ label: LABELS.deleteProject, icon: <DeleteOutlineIcon fontSize="small" />, onClick: onDelete, color: 'error' as const, testId: 'delete-button' }]
            : []),
        ...historyAction,
      ];

  return (
    <div>
      {showDivider && <hr className="border-border-subtle" />}

      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex items-center gap-2 flex-1 min-w-0 text-left bg-transparent border-0 cursor-pointer text-base"
        >
          <ExpandMoreIcon
            className={`text-text-disabled shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
          <span className={`flex-1 min-w-0 ${project.archived ? 'text-text-secondary' : ''}`}>
            <span className="font-medium">{project.name}</span>
            <span className="text-text-disabled ml-2">{project.year}</span>
          </span>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <RowActions actions={actions} />
        </div>
      </div>

      <Collapse in={expanded}>
        <div>
          {addresses.length === 0 && (
            <>
              <hr className="border-t border-border mb-0 mt-0" />
              <p className="text-sm text-text-disabled px-12 py-2">{LABELS.noAddresses}</p>
            </>
          )}
          {addresses.map((a) => (
            <AddressRow
              key={a.id}
              address={a}
              readOnly={project.archived || !onEditAddress}
              onSetDefault={() => onSetDefaultAddress?.(a.id)}
              onEdit={() => onEditAddress?.(a)}
              onDelete={() => onDeleteAddress?.(a.id)}
            />
          ))}
          {!project.archived && onAddAddress && (
            <div className="px-4 py-2 border-t border-border">
              <Button startIcon={<AddIcon />} onClick={onAddAddress} color="primary" data-testid="add-address-button">
                {LABELS.addAddress}
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}
